import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { startCheckout } from "@/lib/checkout";
import { guessCountry } from "@/lib/countries";
import { TZ, prefLabel } from "@/lib/dates";
import { FormProgress } from "@/components/forms/FormKit";
import { AboutYouSection, TimeSection, TopicSection, sectionDone, toDetails, validateBooking } from "@/components/oneonone/BookingSections";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BellRing, CalendarClock, Lock, ShieldCheck, Video } from "lucide-react";

const DRAFT_KEY = "cws:1on1-draft";
const EMPTY = { name: "", age: "", country: "", phone: "", reason: "", notes: "", days: [], window: "", from: "", to: "", timezone: TZ, lead_id: "" };
const STEPS = [
  { t: "Tell us about you", d: "Share a few details, what you'd like to talk about, and the days and times that suit you." },
  { t: "Pay securely", d: "Checkout takes under a minute and you get an instant confirmation email." },
  { t: "We confirm your time", d: "Sankalp's team picks a slot in your window, emails you the time and call link, then sends a reminder a day before." },
];

function loadDraft() {
  const base = { ...EMPTY, country: guessCountry() };
  try { return { ...base, ...JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "{}") }; } catch { return base; }
}

export default function OneOnOne() {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState(loadDraft);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const { currency, format } = useCurrency();
  const nav = useNavigate();
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => { api.get("/catalog/one-on-one").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (user?.name) { setForm((f) => (f.name ? f : { ...f, name: user.name })); } }, [user]);

  const done = sectionDone(form);
  const price = info ? format(info[`price_${currency}`]) : "";

  const toLogin = async () => {
    let leadId = form.lead_id;
    try { if (!leadId) { leadId = (await api.post("/one-on-one/requests", toDetails(form))).data.request_id; } } catch { leadId = ""; }
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ ...form, lead_id: leadId }));
    toast.info("Please sign in to finish booking. Your answers are saved.");
    nav("/login?next=/meet-sankalp/1-on-1");
  };

  const pay = async (e) => {
    e.preventDefault();
    const err = validateBooking(form);
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      if (!user) { await toLogin(); return; }
      sessionStorage.removeItem(DRAFT_KEY);
      await startCheckout({ item_kind: "one_on_one", item_id: "one-on-one-60", currency, details: toDetails(form), lead_id: form.lead_id || undefined });
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Checkout failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg-adult" />
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="absolute -top-40 right-0 w-[36rem] h-[36rem] rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 pt-10 pb-20 relative">
          <Link to="/meet-sankalp" data-testid="one-on-one-back" className="group inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-amber-400 transition-colors">
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" /> Meet Sankalp
          </Link>
          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-start">
            <OneOnOneIntro />
            <form onSubmit={pay} data-testid="one-on-one-form" className="lg:col-span-7 relative rounded-3xl border border-zinc-800 bg-zinc-950/90 backdrop-blur-xl p-5 sm:p-8 shadow-[0_30px_80px_-40px_rgba(245,158,11,0.55)]">
              <div aria-hidden className="absolute -top-px left-10 right-10 h-px bg-gradient-to-r from-transparent via-amber-500/80 to-transparent" />
              <FormProgress done={Object.values(done).filter(Boolean).length} total={3} />
              <AboutYouSection form={form} set={set} done={done.about} />
              <TopicSection form={form} set={set} done={done.topic} reasons={info?.reasons || []} />
              <TimeSection form={form} set={set} done={done.time} />
              <PaySummary price={price} pref={prefLabel(form)} busy={busy} ready={!!info} />
            </form>
          </div>
        </div>
      </section>
      <HowItWorks />
      <Footer variant="adult" />
    </div>
  );
}

function PaySummary({ price, pref, busy, ready }) {
  return (
    <div className="mt-8 rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/10 via-transparent to-violet-500/10 p-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Session fee</div>
          <div data-testid="one-on-one-price" className="font-adult-mono text-3xl font-semibold text-amber-400 mt-1">{price}</div>
        </div>
        <div className="text-xs text-zinc-400 font-adult-mono text-right">60 min · video call</div>
      </div>
      {pref && (
        <div data-testid="one-on-one-pref-summary" className="mt-3 flex items-start gap-2 text-xs text-zinc-300">
          <CalendarClock className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />{pref}
        </div>
      )}
      <Button type="submit" data-testid="one-on-one-pay-btn" disabled={busy || !ready} className="group w-full mt-5 h-12 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold text-base glow-amber">
        {busy ? "Please wait…" : <>Pay {price} &amp; book <ArrowRight className="w-4 h-4 ml-1.5 transition-transform group-hover:translate-x-1" /></>}
      </Button>
      <p className="mt-3 text-[11px] text-zinc-500 text-center flex items-center justify-center gap-1.5">
        <Lock className="w-3 h-3 shrink-0" />Sankalp&apos;s team confirms your time by email, plus a reminder a day before.
      </p>
    </div>
  );
}

function WeekArt() {
  const on = new Set([7, 8, 9, 10, 11, 18, 19]);
  return (
    <div aria-hidden className="mt-10 hidden lg:block w-full max-w-sm rounded-2xl border border-zinc-800 bg-black/40 p-5">
      <div className="flex items-center justify-between text-[10px] font-adult-mono uppercase tracking-[0.2em] text-zinc-500">
        <span>Your week</span><span className="text-amber-400">6 – 9 PM</span>
      </div>
      <div className="mt-4 grid grid-cols-7 gap-1.5">
        {["M", "T", "W", "T", "F", "S", "S"].map((d, i) => <div key={i} className="text-center text-[10px] font-adult-mono text-zinc-600">{d}</div>)}
        {Array.from({ length: 21 }, (_, i) => (
          <div key={i} className={`h-6 rounded-md transition-colors ${on.has(i) ? "bg-amber-500/70 shadow-[0_0_12px_rgba(245,158,11,0.45)]" : "bg-zinc-800/60"}`} />
        ))}
      </div>
    </div>
  );
}

function OneOnOneIntro() {
  const facts = [
    { Icon: Video, t: "60-minute video call" },
    { Icon: CalendarClock, t: "You share the days & times that suit you" },
    { Icon: BellRing, t: "Reminder email a day before" },
    { Icon: ShieldCheck, t: "Private, one-to-one" },
  ];
  return (
    <div className="lg:col-span-5 lg:pt-6 lg:sticky lg:top-24">
      <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-4">1-on-1 with Sankalp</div>
      <h1 data-testid="one-on-one-title" className="font-adult-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.02]">
        An hour, just you <span className="font-adult-serif italic text-amber-400">and Sankalp.</span>
      </h1>
      <p className="mt-6 text-zinc-300 text-lg leading-relaxed max-w-xl">
        Stuck on a career move, a tech problem, or something heavier on the heart? Book a private 60-minute video call to talk it through and leave with clear next steps.
      </p>
      <div className="mt-8 grid grid-cols-2 gap-3 max-w-xl">
        {facts.map(({ Icon, t }) => (
          <div key={t} className="rounded-xl border border-zinc-800 bg-black/40 p-4 text-sm text-zinc-300 transition-colors hover:border-amber-500/40">
            <Icon className="w-4 h-4 text-amber-400 mb-2" />{t}
          </div>
        ))}
      </div>
      <WeekArt />
    </div>
  );
}

function HowItWorks() {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-24">
      <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-adult-mono mb-6">How it works</div>
      <div className="grid md:grid-cols-3 gap-4">
        {STEPS.map((s, i) => (
          <div key={s.t} className="rounded-2xl border border-zinc-800 p-6 hover:border-amber-500/40 transition-smooth">
            <div className="font-adult-mono text-amber-400 text-sm">0{i + 1}</div>
            <div className="mt-3 font-adult-heading text-xl">{s.t}</div>
            <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  );
}
